import _ from "lodash";

import type {AzureBaseMap} from "../module/AzureBaseMap";
import type {MapMouseEvent} from "azure-maps-control";
import type {OverlayFeatureClickEvent} from "@esWebAPI/es/model/Models";
import type {TerritoryLayerCollection} from "@esWebAPI/es/map/layer/territory/TerritoryLayerCollection";
import type {ITerritoryLayer} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";

import {TerritoryLayerUtils} from "@esWebAPI/es/map/layer/territory/TerritoryLayerUtils";
import {isPieClusteringOn, LAYER_DATA_LIMITS} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {RouteLayer} from "@esWebAPI/es/map/layer/route/RouteLayer";
import {StopSymbolLayer} from "@esWebAPI/es/map/layer/route/StopSymbolLayer";

export class AzureClickFeatureHandler {

    private readonly baseMapModule: AzureBaseMap;
    private readonly territoryLayerCollection: TerritoryLayerCollection|null;
    private _selectionEnabled: boolean = true;

    private readonly _eventChannel = es.util.createEventChannel({
        onClick: 1,
        onDblClick: 1,
        onFindFeature: 1,
        onFindFeatures: 1,
        onTerritoryFeaturePlusMinusClick: 1,
        onOverlayFeatureClick: 1,
        onDestroy: 1
    }, "es.map.handler.ClickFeatureHandler");

    constructor(baseMapModule: AzureBaseMap, territoryLayerCollection: TerritoryLayerCollection|null) {
        this.baseMapModule = baseMapModule;
        this.territoryLayerCollection = territoryLayerCollection;

        this.onClick = this.onClick.bind(this);
        //this.onDblClick = this.onDblClick.bind(this);

        baseMapModule.map.events.add('click', this.onClick);
        //baseMapModule.map.events.add('dblclick', this.onDblClick);
    }

    get selectionEnabled(): boolean {
        return this._selectionEnabled;
    }

    set selectionEnabled(value: boolean) {
        this._selectionEnabled = value;
    }

    // private onDblClick(e: MapMouseEvent) {
    //     es.log.log("onDblClick", e);
    // }

    /**
     * Enforce a max num if results that can be returned from a user click.
     * @param featIds
     */
    private doEnforceFeatIdLimit = (featIds: number[]) => {
        const max = LAYER_DATA_LIMITS.MAX_FEATURE_RESULTS_FROM_A_CLICK;
        if (featIds.length > max) {
            es.log.warn(`Max allowable features to select is ${max}. Requested: ${featIds.length}`);
            return featIds.slice(0, max);
        }
        return featIds;
    }

    private onClick(e: MapMouseEvent) {

        let shapes: any[] = [];
        let suppressEvents = !this.selectionEnabled;

        if (e.shapes) {
            e.shapes.forEach((shape: any) => {
                const layer = shape.layer ? this.baseMapModule.getLayerById(shape.layer.id) : undefined;

                // If layer is null it is not ours (it's Azure), special case for overlay layers and territory labels layer.
                // Filter out Azure internal and non-selectable features.
                if (layer) {
                    let selectable = layer.layerView.selectable && layer.isVisibleRes();

                    if (this.territoryLayerCollection && !this.territoryLayerCollection.isComparisonPage()) {
                        const territoryModel = this.territoryLayerCollection.getTerritoryModel();

                        const isTerritoryBaseRelatedOrOutline =
                            territoryModel.isBaseOrRelatedLayer(layer.layerView.id) ||
                            territoryModel.isOutlinesLayer(layer.layerView.id);

                        const ter = !territoryModel.isAccountOnly() && territoryModel.isBaseLayer(layer.layerView.id) ?
                            territoryModel.getByBaseFeatureId(shape.id, territoryModel.territoryMeta.levels) : null;

                        selectable = selectable && (
                            !isTerritoryBaseRelatedOrOutline ||
                            TerritoryLayerUtils.isSelectable(layer as ITerritoryLayer, shape, ter)
                        );
                    }

                    if (selectable) {
                        shape.layerViewId = layer.layerView.id;
                        shapes.push(shape);
                    }
                }

                // Special-case for temp address points and territory + symbol which are on the overlay.
                // TODO: AzureOverlay.SYMBOL_LAYER_ID etc is undefined in public map
                if (shape.layer && ["overlay-symbol", "overlay-polygon", "overlay-line"].includes(shape.layer.id)){
                    if (shape.properties.suppressEvents) {
                        suppressEvents = true;
                    }
                    this.eventChannel.fireEvent("onOverlayFeatureClick", {shape: shape, pixel: e.pixel, position: e.position} as OverlayFeatureClickEvent);
                }
            });
        }

        if (suppressEvents){
            return;
        }

        //Prevent duplicates when the same feature is on multiple layers (eg. Polygon + Stroke)
        shapes = _.uniqWith(shapes, (a, b) => a.id === b.id && a.layerViewId == b.layerViewId);

        //if (es.isDeveloper) {
            es.log.log(shapes);
        //}

        // If more than one shape is clicked
        if (shapes.length > 1) {
            const layers: any = [];
            const layerViewIds: number[] = _.uniq(shapes.map(f => f.layerViewId));
            const layersDfds = layerViewIds.map(layerViewId => {
                // Get the feature ids of the clicked shapes, including children of clusters
                return this.getShapesFeatureIds(layerViewId, shapes).then((...args) => {
                    let featureIds = _.sortBy(_.concat(...args));
                    featureIds = this.doEnforceFeatIdLimit(featureIds);
                    const layerView = this.baseMapModule.getLayersByLayerViewId(layerViewId)[0]?.layerView;
                    if (!layerView) {
                        throw new Error("No layerView for layerViewId: " + layerViewId);
                    }

                    return es.api.feature.getByIds(layerViewId, featureIds, null).then((feats) => {
                        return feats;
                    });
                });
            });

            Promise.all(layersDfds).then((args) => {
                args.forEach((arg) => {
                    const feats = Array.isArray(arg[0]) ? arg[0] : arg;
                    if (Array.isArray(feats) && feats.length > 0) {
                        layers.push({
                            layerViewId: feats[0].layerViewId,
                            features: feats
                        });
                    }
                });
                this.eventChannel.fireEvent("onFindFeatures", layers, e.position);
            });
        }
        // If only one shape is clicked
        else if (shapes.length == 1) {
            // If the shape is a cluster we fetch the ids of the leaves
            if (shapes[0].properties.cluster === true) {
                const layerViewId = shapes[0].layerViewId;
                this.getShapesFeatureIds(layerViewId, shapes).then((featureIds) => {
                    featureIds = this.doEnforceFeatIdLimit(featureIds);
                    es.api.feature.getByIds(layerViewId, featureIds, null).then((feats: any[]) => {
                        const layers = [{layerViewId: layerViewId, features: feats}];
                        this.eventChannel.fireEvent("onFindFeatures", layers, e.position);
                    });
                });
            } else {

                // Fetch the full feature and fire the event
                const triggerSingle = () => {
                    es.api.feature.get(shapes[0].layerViewId, shapes[0].id).then((feature: any) => {
                        if(!feature){
                            console.warn("Failed to fetch feature", shapes);
                        }
                        this.eventChannel.fireEvent("onFindFeature", feature, e.position);
                    });
                };


                const layers = this.baseMapModule.getLayersByLayerViewId(shapes[0].layerViewId);

                const scheduleLayer = layers.find(l => l instanceof RouteLayer) as RouteLayer;
                const scheduleSymbolStopLayer =
                    (layers.find(l => l instanceof StopSymbolLayer) || scheduleLayer?.stopLayer) as StopSymbolLayer;


                if (scheduleSymbolStopLayer && scheduleSymbolStopLayer.getLayerIds().includes(shapes[0].layer.id)) {
                    const pointLayer = (layers.find(l => l instanceof PointLayer) || scheduleLayer?.pointLayer) as PointLayer;

                    // const clusteredLayerId = isPieClusteringOn(pointLayer.layerView) && pointLayer.clusterLayer ?
                    //     pointLayer.clusterLayer.getId() : pointLayer.getActiveLayerId();
                    const clusteredLayerId = isPieClusteringOn(pointLayer.layerView) ?
                        pointLayer.pieClusterLayer?.getLayerIds()[0] : pointLayer.getLayerIds()[0];

                    const cluster = this.baseMapModule.getMapLibreMap()
                        .queryRenderedFeatures({layers: [clusteredLayerId]})
                        .filter((f: any) => f.properties.cluster && f.properties['FEAT_IDS'].includes(`,${shapes[0].id},`))
                        .map((f:any) => {
                            return {...f, layerViewId: pointLayer.layerView.id}
                        })

                    if (cluster.length > 0) {
                        this.getShapesFeatureIds(pointLayer.layerView.id, cluster).then((featureIds) => {
                            featureIds = this.doEnforceFeatIdLimit(featureIds);
                            es.api.feature.getByIds(pointLayer.layerView.id, featureIds, null).then((feats: any[]) => {
                                const layers = [{layerViewId: pointLayer.layerView.id, features: feats}];
                                this.eventChannel.fireEvent("onFindFeatures", layers, e.position);
                            });
                        });
                        return;
                    }
                }

                const layer = this.baseMapModule.getLayersByLayerViewId(shapes[0].layerViewId)[0] as ITerritoryLayer;

                // On territory layers, if the polygon feature contains an assignment we show the select list
                if (!this.territoryLayerCollection ||
                    !this.territoryLayerCollection.getTerritoryModel().isBaseOrRelatedLayer(layer.layerView.id) ||
                    this.territoryLayerCollection.getTerritoryModel().isAccountOnly()) {
                    triggerSingle();
                    return;
                }

                //Get territories assignments for the polygon feature
                const territoryModel = this.territoryLayerCollection.getTerritoryModel();
                this.territoryLayerCollection.getTerritoriesAssociatedByLayerViewAndFeatureId(shapes[0].layerViewId, shapes[0].id).then((territories) => {

                    territories = territories.filter(t => !territoryModel.getFilter() || territoryModel.getFilter()?.includes(t.ES_ID));

                    if (territories.length === 0) {
                        // If there is no assignments we fire the normal click event

                        if (!layer || TerritoryLayerUtils.isSelectable(layer, shapes[0])) {
                            triggerSingle();
                        }
                        return;
                    }
                    // Otherwise fire onFindFeatures to show the list
                    es.api.feature.get(shapes[0].layerViewId, shapes[0].id).then((feature: any) => {
                        if(!feature){
                            console.warn("Failed to fetch feature", shapes);
                        }
                        const layers = [{layerViewId: shapes[0].layerViewId, features: [feature]}];
                        this.eventChannel.fireEvent("onFindFeatures", layers, e.position);
                    });
                });
            }
        } else {
            this.eventChannel.fireEvent("onClick", e.pixel);
            es.log.log("onClick miss", e.position, e.shapes);
        }
    }

    get eventChannel() {
        return this._eventChannel;
    }

    /**
     * Get the feature id corresponding the clicked shapes, if a shape is a cluster fetch the feature ids of the leaves.
     * @param layerViewId LayerView ID
     * @param shapes Clicked shapes
     * @private
     */
    private getShapesFeatureIds(layerViewId: number, shapes: any[]) : JQuery.Promise<number[]> {
        const featIdsDfds: any = [];
        shapes.filter(s => s.layerViewId === layerViewId).forEach(s => {
            const featIdsDfd = $.Deferred();
            if (s.properties.cluster === true) {
                const pointLayer = this.baseMapModule.getLayersByLayerViewId(layerViewId)[0] as PointLayer;
                pointLayer.getClusterLeavesFeatIds(s.properties.cluster_id).then(rs => {
                    featIdsDfd.resolve(rs);
                });
            } else {
                featIdsDfd.resolve([s.id]);
            }
            featIdsDfds.push(featIdsDfd);
        });

        return $.when.apply($, featIdsDfds).then((...args) => {
            const featureIds = _.sortBy(_.concat(...args));
            //console.log("getFeatureIds", layerViewId, shapes, featureIds);
            return es.util.deferred.resolved(featureIds);
        });
    }

    public destroy(){
        this.baseMapModule.map.events.remove('click', this.onClick as any)
        //this.baseMapModule.map.events.remove('dblclick', this.onDblClick as any)
        this._eventChannel.fireEvent("onDestroy")
        this._eventChannel.destroy();
    }
}
